import { useState, type ReactNode } from 'react';
import { horasEntreHHmm, cruzaMedianoche } from './horas.ts';

// Selector de horario del evento: hora de inicio + duración (6/8/9 h o libre).
// Los eventos pueden cruzar medianoche; en ese caso la hora de fin cae al día
// siguiente y se avisa con "+1 día". El componente sigue exponiendo hora_inicio
// y hora_fin (el contrato del backend no cambia; la duración es solo de entrada).

const DURACIONES = [6, 8, 9];

function sumarHoras(hhmm: string, horas: number): string {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  const total = (((h || 0) * 60 + (m || 0)) + Math.round(horas * 60)) % (24 * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function SelectorHorario({
  horaInicio,
  horaFin,
  onChange,
  colorActivo = 'bg-primario-400',
}: {
  horaInicio: string;
  horaFin: string;
  onChange: (inicio: string, fin: string) => void;
  colorActivo?: string;
}): ReactNode {
  const durActual = horasEntreHHmm(horaInicio, horaFin);
  const [libre, setLibre] = useState(!DURACIONES.includes(durActual));

  function cambiarInicio(nuevo: string) {
    // Si hay una duración fija elegida, se recalcula el fin para mantenerla.
    if (!libre && DURACIONES.includes(durActual)) {
      onChange(nuevo, sumarHoras(nuevo, durActual));
    } else {
      onChange(nuevo, horaFin);
    }
  }

  function elegirDuracion(horas: number) {
    setLibre(false);
    onChange(horaInicio, sumarHoras(horaInicio, horas));
  }

  const cruza = cruzaMedianoche(horaInicio, horaFin);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Desde *</span>
          <input className="campo" type="time" required value={horaInicio} onChange={(e) => cambiarInicio(e.target.value)} />
        </label>
        <div className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Termina</span>
          <div className="campo flex items-center bg-superficie-100/60">
            <span className="font-semibold text-neutro-800">{horaFin}</span>
          </div>
        </div>
      </div>

      <div>
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Duración</span>
        <div className="flex flex-wrap gap-2">
          {DURACIONES.map((d) => {
            const activo = !libre && durActual === d;
            return (
              <button
                key={d}
                type="button"
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  activo ? `${colorActivo} text-white shadow-sm` : 'border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100'
                }`}
                onClick={() => elegirDuracion(d)}
              >
                {d} h
              </button>
            );
          })}
          <button
            type="button"
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
              libre ? `${colorActivo} text-white shadow-sm` : 'border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100'
            }`}
            onClick={() => setLibre(true)}
          >
            Otra
          </button>
          {libre && (
            <input
              className="campo w-28"
              type="time"
              required
              value={horaFin}
              onChange={(e) => onChange(horaInicio, e.target.value)}
              aria-label="Hora de fin"
            />
          )}
        </div>
        <p className="mt-1.5 text-xs text-neutro-400">
          {horasEntreHHmm(horaInicio, horaFin).toLocaleString('es-UY')} h en total
          {cruza && ' · termina de madrugada, al día siguiente'}
        </p>
      </div>
    </div>
  );
}
